import { isNumeric } from '../../util/helpers';
import { parseLatLon } from '../util/parseUtils';
import { LandingSurface } from './landing-surface.model';
import { PatternAltitude } from './pattern-altitude.model';
import { PilotControlledLighting } from './pilot-controlled-lighting.model';
import { Contact } from './contact.model';
import { AirportComment } from './airport-comment.model';
import { NoiseAbatement } from './noise-abatement.model';
import { ProcedureType, TerminalProcedurePack } from './terminal-procedure.model';
import { AirportReview } from './airport-review.model';
import { AirportTag } from './airport-tag.model';
import { BusinessSummary } from './business-summary.model';
import { FlyingclubSummary } from './flyingclub-summary.model';
import { AirportNavaid } from './airport-navaid.model';
import { FlightComm } from './flight-comm.model';
import { LocalAttraction } from './local-attraction.model';
import { AirportAlert } from './airport-alert.model';
import { BusinessAdmin } from '../../business/models/business-admin.model';
import { AfmOther } from './afm-other.model';
import { Webcam } from './webcam.model';
import { NOTAM } from './notam.model';
/**
 * Airport operational status
 */
export var AirportStatus;
(function (AirportStatus) {
    AirportStatus[AirportStatus["Operational"] = 0] = "Operational";
    AirportStatus[AirportStatus["ClosedIndefinitely"] = 1] = "ClosedIndefinitely";
    AirportStatus[AirportStatus["ClosedPermanently"] = 2] = "ClosedPermanently";
})(AirportStatus || (AirportStatus = {}));
/**
 * Airport type
 */
export var AirportType;
(function (AirportType) {
    AirportType["Heliport"] = "heliport";
    AirportType["Ultralight"] = "ultralight";
    AirportType["GliderPort"] = "gliderport";
    AirportType["BalloonPort"] = "balloonport";
    AirportType["Airport"] = "airport";
    AirportType["SeaplaneBase"] = "seaport";
})(AirportType || (AirportType = {}));
/**
 * Tower facility types
 */
export var TowerFacilityType;
(function (TowerFacilityType) {
    /**
     * Air Traffic Control Tower
     */
    TowerFacilityType[TowerFacilityType["ATCT"] = 0] = "ATCT";
    /**
     * Non-ATC Providing IFR Service
     */
    TowerFacilityType[TowerFacilityType["NON_ATCT"] = 1] = "NON_ATCT";
    /**
     * ATCT plus Approach Control
     */
    TowerFacilityType[TowerFacilityType["ATCT_AC"] = 2] = "ATCT_AC";
    /**
     * ATCT (Air Force) plus Radar Approach Control (Air Force operates ATCT, FAA operates approach control).
     */
    TowerFacilityType[TowerFacilityType["ATCT_RAPCON"] = 3] = "ATCT_RAPCON";
    /**
     * ATCT (Navy) plus Radar Approach Control (Navy operates ATCT, FAA operates approach control).
     */
    TowerFacilityType[TowerFacilityType["ATCT_RATCF"] = 4] = "ATCT_RATCF";
    /**
     * ATCT plus Terminal Radar Approach Control
     */
    TowerFacilityType[TowerFacilityType["ATCT_TRACON"] = 5] = "ATCT_TRACON";
})(TowerFacilityType || (TowerFacilityType = {}));
/**
 * Airport ownership type
 */
export var AirportOwnershipType;
(function (AirportOwnershipType) {
    AirportOwnershipType[AirportOwnershipType["Civil"] = 0] = "Civil";
    AirportOwnershipType[AirportOwnershipType["Private"] = 1] = "Private";
    AirportOwnershipType[AirportOwnershipType["Army"] = 2] = "Army";
    AirportOwnershipType[AirportOwnershipType["Navy"] = 3] = "Navy";
    AirportOwnershipType[AirportOwnershipType["USAF"] = 4] = "USAF";
    AirportOwnershipType[AirportOwnershipType["CoastGuard"] = 5] = "CoastGuard";
})(AirportOwnershipType || (AirportOwnershipType = {}));
/**
 * Method used to determine airport elevation
 */
export var ElevationDeterminationMethod;
(function (ElevationDeterminationMethod) {
    ElevationDeterminationMethod[ElevationDeterminationMethod["Estimated"] = 0] = "Estimated";
    ElevationDeterminationMethod[ElevationDeterminationMethod["Surveyed"] = 1] = "Surveyed";
})(ElevationDeterminationMethod || (ElevationDeterminationMethod = {}));
/**
 * Regex for parsing out the magnetic variation value and direction from a string.
 */
export var MAG_VARIATION_REGEX = /(\d+)([EW])/;
/**
 * Airport model
 */
var Airport = /** @class */ (function () {
    function Airport() {
    }
    Object.defineProperty(Airport.prototype, "magVariationValue", {
        /**
         * Magnetic variation as a numeric value (positive if Westerly, negative if Easterly, or null if not reported)
         */
        get: function () {
            var match = MAG_VARIATION_REGEX.exec(this.magVariation || '');
            if (!match) {
                return null;
            }
            var value = parseInt(match[1], 10);
            var sign = match[2] === 'W' ? 1 : -1;
            return sign * value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Airport.prototype, "identifier", {
        /**
         * Gets the identifier that should be displayed for this airport. This will either be the ICAO identifier if
         * available, or the FAA identifier otherwise.
         */
        get: function () {
            return this.icaoId || this.faaId || '';
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Airport.prototype, "diagram", {
        /**
         * Returns the airport diagram for the airport if one is available, or null otherwise
         * @returns {TerminalProcedure|null}
         */
        get: function () {
            return (this.terminalProcedures.current.procedures || [])
                .find(function (p) { return p.type === ProcedureType.AirportDiagram; }) || null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Airport.prototype, "hasThumbNail", {
        /**
         * Returns true if the airport has a thumbnail diagram available. This is based on there being at least one
         * approach procedure for the airport, since that is what is used to generate the thumbnails. Note that the thumbNail
         * property will always be populated with a URL (since that is how it is returned from the API), but that link is
         * not guaranteed to be valid unless this property is true.
         * @returns {boolean}
         */
        get: function () {
            return (this.terminalProcedures.current.procedures || []).some(function (p) { return p.type === ProcedureType.IAP; });
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Parses an airport as returned by the API into an instance of the Airport class
     */
    Airport.parse = function (data) {
        return Object.assign(new Airport(), {
            airportId: data.airportId,
            icaoId: data.icaoId,
            faaId: data.faaId,
            status: this.parseAirportStatus(data.status),
            type: this.parseAirportType(data.type),
            name: data.name,
            shortName: data.shortName,
            dataEffectiveDate: data.faaData ? (data.faaData.effectiveDate ? new Date(data.faaData.effectiveDate) : null) : null,
            magVariation: data.magVariation || null,
            magVariationEpochYear: data.magVariationEpochYear,
            artcc: data.artcc,
            artccName: data.artccName,
            airspace: data.airspace || {},
            location: data.location ? parseLatLon(data.location.latitude, data.location.longitude) : null,
            elevation: isNumeric(data.elev) ? +data.elev : null,
            elevationMethod: this.parseElevationMethod(data.elevMethod),
            towered: data.towerPresent,
            towerFacilityType: this.parseTowerFacilityType(data.towerFacilityType),
            towerHrsPerDay: isNumeric(data.towerHrsPerDay) ? +data.towerHrsPerDay : null,
            towerRegularityOfOps: data.towerRegularityOfOps || null,
            masterAirportTower: data.masterAirportTower || null,
            notamIdentifier: data.faaData ? data.faaData.notamIdentifier : null,
            thumbNail: data.thumbNail,
            segmentedCircle: data.segmentedCircle,
            segmentedCircleLighted: data.segmentedCircleLighted,
            windIndicator: data.windIndicator,
            windIndicatorLighted: data.windIndicatorLighted,
            beacon: data.beacon,
            beaconColor: data.beaconColor || null,
            attended: data.attended,
            pilotActLighting: data.pilotActLighting,
            mainPhone: data.mainPhone || null,
            email: data.email || null,
            website: data.website || null,
            fssId: data.fssId || null,
            fssPhone: data.fssPhone || null,
            fssName: data.fssName || null,
            alternateFssId: data.alternateFssId || null,
            alternateFssPhone: data.alternateFssPhone || null,
            airportOwnership: this.parseAirportOwnershipType(data.airportOwnership),
            publicUse: this.parsePublicUse(data.airportUsage),
            customsIntlEntry: data.customsIntlEntry,
            customsLandingRights: data.customsLandingRights,
            militaryJoint: data.militaryJoint,
            militaryLandingRights: data.militaryLandingRights,
            address1: data.address1 || null,
            address2: data.address2 || null,
            postalCode: data.zipcode || null,
            city: data.city || null,
            stateProvince: data.stateProvince || null,
            country: data.country || null,
            distanceFromCity: isNumeric(data.distanceFromCity) ? +data.distanceFromCity : null,
            directionFromCity: data.directionFromCity,
            chart: this.parseChart(data.airportCharts),
            landingSurfaces: (data.landingSurfaces || []).map(function (d) { return LandingSurface.parse(d); }),
            patternAltitudes: (data.patternAltitudes || []).map(function (d) { return PatternAltitude.parse(d); }),
            flightComms: this.parseFlightComms(data.flightComms),
            pilotControlledLighting: (data.pilotControlledLightingClicks || []).map(function (d) { return PilotControlledLighting.parse(d); }),
            contacts: (data.contacts || []).map(function (d) { return Contact.parse(d); }),
            airportComments: (data.airportComments || []).map(function (d) { return AirportComment.parse(d); }),
            activity: data.faaData ? this.parseActivity(data.faaData) : null,
            noiseAbatement: (data.afmNoises || []).map(function (d) { return NoiseAbatement.parse(d); }),
            terminalProcedures: TerminalProcedurePack.parse(data.terminalProcedures, data.terminalProceduresNext),
            reviews: (data.airportReviews || []).map(function (d) { return AirportReview.parse(d); }),
            reviewSummary: data.reviewSummary,
            airportTags: (data.airportTags || []).map(function (d) { return AirportTag.parse(d); }),
            businesses: (data.businesses || []).map(function (d) { return BusinessSummary.parse(d); }),
            flyingClubs: (data.flyingClubs || []).map(function (d) { return FlyingclubSummary.parse(d); }),
            localAttractions: (data.afmTravels || []).map(function (d) { return LocalAttraction.parse(d); }),
            navaids: (data.navaidsNearAirport || []).map(function (d) { return AirportNavaid.parse(d); }),
            alerts: (data.alerts || []).map(function (d) { return AirportAlert.parse(d); }),
            admins: (data.airportAdmins || []).map(function (d) { return BusinessAdmin.parse(d); }),
            afmOther: data.afmOther ? AfmOther.parse(data.afmOther) : null,
            webcams: (data.webcams || []).map(function (d) { return Webcam.parse(d); }),
            businessFees: data.businessFees || null,
            timeZone: data.timeZone,
            sourceId: data.sourceId,
            pilotAcctLightingFreq: data.pilotAcctLightingFreq,
            bodyOfWater: data.bodyOfWater,
            geoState: data.geoState,
            islandProvince: data.islandProvince,
            fteAwardYear: data.fteAwardYear,
            notamsUS: (data.notaMsUS || []).map(function (d) { return NOTAM.parse(d); }),
            notamsInternational: (data.notaMsInternational || []).map(function (d) { return NOTAM.parse(d); }),
            tfrs: (data.tfRs || []).map(function (d) { return NOTAM.parse(d); }),
            notamCountUS: data.notamCountUS,
            notamCountInternational: data.notamCountInternational
        });
    };
    /**
     * Parses airport operation status from string
     * @param status {string} Airport status as returned by API
     * @return {AirportStatus} Parsed airport status
     */
    Airport.parseAirportStatus = function (status) {
        switch (status) {
            case 'operational':
                return AirportStatus.Operational;
            case 'closed indefinitely':
                return AirportStatus.ClosedIndefinitely;
            case 'closed permanently':
                return AirportStatus.ClosedPermanently;
            default:
                return null;
        }
    };
    /**
     * Formats airport operation status for display
     * @param status {AirportStatus} Airport status
     * @return {string} Formatted airport status
     */
    Airport.formatAirportStatus = function (status) {
        switch (status) {
            case AirportStatus.Operational:
                return 'Operational';
            case AirportStatus.ClosedIndefinitely:
                return 'Closed Indefinitely';
            case AirportStatus.ClosedPermanently:
                return 'Closed Permanently';
            default:
                return null;
        }
    };
    /**
     * Parses airport type from string
     * @param type {string} Airport type as returned by API
     * @return {AirportType} Parsed airport type
     */
    Airport.parseAirportType = function (type) {
        switch (type) {
            case 'heliport':
                return AirportType.Heliport;
            case 'ultralight':
                return AirportType.Ultralight;
            case 'gliderport':
                return AirportType.GliderPort;
            case 'balloonport':
                return AirportType.BalloonPort;
            case 'airport':
                return AirportType.Airport;
            case 'seaplane base':
                return AirportType.SeaplaneBase;
            default:
                return null;
        }
    };
    /**
     * Parses tower facility type from string
     * @param type {string} Tower facility type as returned by API
     * @return {TowerFacilityType} Parsed tower facility type
     */
    Airport.parseTowerFacilityType = function (type) {
        switch (type) {
            case 'ATCT(Navy) plus Radar Approach Control':
                return TowerFacilityType.ATCT_RATCF;
            case 'ATCT(Air Force) plus Radar Approach Control':
                return TowerFacilityType.ATCT_RAPCON;
            case 'ATCT plus Terminal Radar Approach Control':
                return TowerFacilityType.ATCT_TRACON;
            case 'Non-ATC Providing IFR Service':
                return TowerFacilityType.NON_ATCT;
            case 'ATCT plus Approach Control':
                return TowerFacilityType.ATCT_AC;
            case 'Air Traffic Control Tower':
                return TowerFacilityType.ATCT;
            default:
                return null;
        }
    };
    /**
     * Formats tower facility type for display
     * @param value {TowerFacilityType} Tower facility type
     * @return {string} Formatted tower facility type
     */
    Airport.formatTowerFacilityType = function (value) {
        switch (value) {
            case TowerFacilityType.ATCT_RATCF:
                return 'ATCT (Navy) plus Radar Approach Control';
            case TowerFacilityType.ATCT_RAPCON:
                return 'ATCT (Air Force) plus Radar Approach Control';
            case TowerFacilityType.ATCT_TRACON:
                return 'ATCT plus Terminal Radar Approach Control';
            case TowerFacilityType.NON_ATCT:
                return 'Non-ATC Providing IFR Service';
            case TowerFacilityType.ATCT_AC:
                return 'ATCT plus Approach Control';
            case TowerFacilityType.ATCT:
                return 'Air Traffic Control Tower';
            default:
                return null;
        }
    };
    /**
     * Parses airport ownership type from string
     * @param type {string} Airport ownership type as returned by API
     * @return {AirportOwnershipType} Parsed airport ownership type
     */
    Airport.parseAirportOwnershipType = function (type) {
        switch (type) {
            case 'civil':
                return AirportOwnershipType.Civil;
            case 'private':
                return AirportOwnershipType.Private;
            case 'army':
                return AirportOwnershipType.Army;
            case 'navy':
                return AirportOwnershipType.Navy;
            case 'usaf':
                return AirportOwnershipType.USAF;
            case 'coast guard':
                return AirportOwnershipType.CoastGuard;
            default:
                return null;
        }
    };
    /**
     * Formats airport ownership type for display
     * @param type {AirportOwnershipType} Airport ownership type
     * @return {string} Formatted airport ownership type
     */
    Airport.formatAirportOwnershipType = function (type) {
        switch (type) {
            case AirportOwnershipType.Civil:
                return 'Civil';
            case AirportOwnershipType.Private:
                return 'Private';
            case AirportOwnershipType.Army:
                return 'Army';
            case AirportOwnershipType.Navy:
                return 'Navy';
            case AirportOwnershipType.USAF:
                return 'USAF';
            case AirportOwnershipType.CoastGuard:
                return 'Coast Guard';
            default:
                return null;
        }
    };
    /**
     * Parses airport usage type from string
     * @param type {string} Airport usage type as returned by API
     * @return {boolean} Parsed airport usage type
     */
    Airport.parsePublicUse = function (type) {
        switch (type) {
            case 'public':
                return true;
            case 'private':
                return false;
            default:
                return null;
        }
    };
    /**
     * Parses elevation determination method from string
     * @param type {string} Elevation determination method as returned by API
     * @return {ElevationDeterminationMethod} Parsed elevation determination method
     */
    Airport.parseElevationMethod = function (type) {
        switch (type) {
            case 'Estimated':
                return ElevationDeterminationMethod.Estimated;
            case 'Surveyed':
                return ElevationDeterminationMethod.Surveyed;
            default:
                return null;
        }
    };
    /**
     * Formats elevation determination method for display
     * @param value {ElevationDeterminationMethod} Elevation determination method
     * @return {string} Formatted elevation determination method
     */
    Airport.formatElevationMethod = function (value) {
        switch (value) {
            case ElevationDeterminationMethod.Estimated:
                return 'Estimated';
            case ElevationDeterminationMethod.Surveyed:
                return 'Surveyed';
            default:
                return null;
        }
    };
    /**
     * Parses which sectional chart the airport appears in (if any)
     * @param data {any[]} Airport chart data as returned by API
     * @return {string} Chart publisher, name, and type
     */
    Airport.parseChart = function (data) {
        if (!data || data.length < 1) {
            return null;
        }
        // Assume only a single chart per airport (API data model implies there may be more than one, but it is never the
        // case since it's a one-to-one relationship in the source FAA data).
        var chart = data[0].chart;
        if (chart) {
            var publisher = chart.publisher, chartName = chart.chartName, chartType = chart.chartType;
            return publisher + ": " + chartName + " " + chartType;
        }
        return null;
    };
    /**
     * Parses flight communication channels at airport
     * @param data {any[]} Flight comm data as returned by API
     * @return {FlightComm[]} Parsed flight comms
     */
    Airport.parseFlightComms = function (data) {
        if (!data) {
            return [];
        }
        // For each flight comm, the API currently returns a list of one or more frequencies. There are only a handful of
        // cases where a single communication "channel" (e.g., the ASOS weather station) is associated with multiple
        // frequencies, and it's conceptually simpler to display them as two separate communication channels anyway. So
        // any flight comm channel that has multiple frequencies will be returned as a separate FlightComm instance.
        var result = [];
        for (var _i = 0, data_1 = data; _i < data_1.length; _i++) {
            var item = data_1[_i];
            for (var _a = 0, _b = item.frequencies; _a < _b.length; _a++) {
                var freq = _b[_a];
                result.push(FlightComm.parse({
                    flightCommId: item.flightCommId,
                    airportId: item.airportId,
                    service: item.service,
                    radioCall: item.radioCall,
                    shortName: item.shortName,
                    frequencyId: freq.frequencyId,
                    freqMode: freq.freqMode,
                    frequency: freq.frequency,
                    sector: freq.sector,
                    phoneNumber: freq.phoneNumber,
                    phoneType: freq.phoneType
                }));
            }
        }
        return result;
    };
    /**
     * Parses aircraft activity data for airport
     * @param data {any} FAA data as returned by API
     * @return {AirportActivity} Airport activity data
     */
    Airport.parseActivity = function (data) {
        return {
            activityEndingDate: data.activityEndingDate ? new Date(data.activityEndingDate) : null,
            gaAircraftSingleEngine: data.gaAircraftSingleEngine,
            gaAircraftMultiEngine: data.gaAircraftMultiEngine,
            gaAircraftJet: data.gaAircraftJet,
            gaAircraftHelo: data.gaAircraftHelo,
            gaAircraftGliders: data.gaAircraftGliders,
            gaAircraftUltralights: data.gaAircraftUltralights,
            activityAirTaxi: data.activityAirTaxi,
            activityGaLocal: data.activityGaLocal,
            activityGaItinerant: data.activityGaItinerant,
            activityMil: data.activityMil,
            activityCommercial: data.activityCommercial,
            activityCommuter: data.activityCommuter
        };
    };
    /**
     * Returns a boolean indicating whether an airport has the passed in landing surface type or not
     * @param type, Type {LandingSurfaceType}
     */
    Airport.prototype.hasLandingSurfaceType = function (type) {
        return this.landingSurfaces.some(function (sfc) { return sfc.type === type; });
    };
    /**
     * Helper method to return airport comments of the specified type.
     * @param {CommentType} type
     * @returns {Array<AirportComment>}
     */
    Airport.prototype.getComments = function (type) {
        return this.airportComments.filter(function (c) { return c.commentType === type; });
    };
    Airport.prototype.serialize = function () {
        return {
            sourceId: this.sourceId,
            name: this.name,
            shortName: this.shortName,
            location: { latitude: this.location.lat, longitude: this.location.lon },
            icaoId: this.icaoId,
            faaId: this.faaId,
            address1: this.address1,
            address2: this.address2,
            city: this.city,
            stateProvince: this.stateProvince,
            zipcode: this.postalCode,
            country: this.country,
            timeZone: this.timeZone,
            type: this.type,
            status: Airport.formatAirportStatus(this.status),
            website: this.website,
            mainPhone: this.mainPhone,
            masterAirportTower: this.masterAirportTower,
            fssName: this.fssName,
            fssId: this.fssId,
            fssPhone: this.fssPhone,
            magVar: this.magVariation,
            magVariationEpochYear: this.magVariationEpochYear,
            windIndicator: this.windIndicator,
            windIndicatorLighted: this.windIndicatorLighted,
            segmentedCircle: this.segmentedCircle,
            segmentedCircleLighted: this.segmentedCircleLighted,
            beacon: this.beacon,
            beaconColor: this.beaconColor,
            airportUsage: this.publicUse === true ? 'public' : 'private',
            airportOwnership: this.airportOwnership,
            elev: this.elevation,
            towerPresent: this.towered,
            towerFacilityType: this.towerFacilityType,
            towerHrsPerDay: this.towerHrsPerDay,
            towerRegularityOfOps: this.towerRegularityOfOps,
            attended: this.attended,
            pilotActLighting: this.pilotActLighting,
            distanceFromCity: this.distanceFromCity,
            directionFromCity: this.directionFromCity,
            pilotAcctLightingFreq: this.pilotAcctLightingFreq,
            bodyOfWater: this.bodyOfWater,
            geoState: this.geoState,
            islandProvince: this.islandProvince
        };
    };
    return Airport;
}());
export { Airport };
